/**
 * Secrets that ride in URLs, and how to take them out before a URL leaves
 * the app for a third party.
 *
 * Three routes carry a bearer token in the path: a request link (`/r/:token`,
 * which lets an anonymous session upload into somebody's library), a team
 * invitation (`/invite/:token`) and a comped invitation (`/start/:token`).
 * Several more carry one in the query: the one-time sign-in code an invite
 * email adds (`?code=`), Firebase's password-reset code (`?oobCode=`), and the
 * Stripe session a checkout returns with (`?session_id=`).
 *
 * Analytics SDKs record the page URL as a matter of course, in page-view
 * events, in session replays and in referrers. The pentest watched a request
 * link's token go to Amplitude that way and then used it to create uploads.
 * Everything that reports a URL runs it through here first, and the pattern
 * is deliberately blunt: a whole path segment goes, and the whole query goes,
 * because a redactor that has to know every parameter name is one new
 * parameter away from leaking again.
 */

/** Path segments that are the credential. `/r/abc` becomes `/r/[token]`. */
const SECRET_SEGMENTS = /\/(r|invite|start)\/[^/?#\s"']+/g;

/**
 * Query parameters that are a credential. Their presence is the signal that a
 * string is a URL worth cutting the query off, since a bare `?` appears in
 * ordinary prose too.
 */
const SECRET_PARAMS = /[?&](code|oobCode|session_id|token|state)=/;

/** Does this string carry anything `redactUrl` would remove? */
export function carriesSecret(value: string): boolean {
  SECRET_SEGMENTS.lastIndex = 0;
  return SECRET_SEGMENTS.test(value) || SECRET_PARAMS.test(value);
}

/**
 * The same URL or path with its secrets taken out. Safe on a full URL, a bare
 * path, or a string that is neither: anything without a secret comes back
 * untouched.
 */
export function redactUrl(value: string): string {
  if (!value || !carriesSecret(value)) return value;
  let out = value.replace(SECRET_SEGMENTS, '/$1/[token]');
  // The query and fragment go whole once the string is known to carry a
  // secret. `?checkout=success` rides along with `?session_id=…`, and losing
  // it costs a dashboard nothing: the purchase is reported as its own event.
  const cut = out.search(/[?#]/);
  if (cut !== -1) out = out.slice(0, cut);
  return out;
}

/**
 * Walk a bag of event properties and redact every string in it, however
 * nested. Used as the last step before an event leaves for Amplitude, so
 * that a URL smuggled in under any property name, by our code or by a
 * plugin's, is caught the same way.
 */
export function redactDeep<T>(value: T): T {
  if (typeof value === 'string') return redactUrl(value) as T;
  if (Array.isArray(value)) return value.map(redactDeep) as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) out[key] = redactDeep(entry);
    return out as T;
  }
  return value;
}
